import { useCallback, useState } from 'react';
import { IconChevronDown } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';

type ChildItem<T extends string | number> = {
  label: string;
  value: T;
};

type Props<T extends number | string> = {
  label: string;
  childrenOptions?: ChildItem<T>[];
  onChange?: () => void;
  onPick: (values: T[]) => void;
  picked: Array<T>;
  checked: boolean;
  semiChecked: boolean;
  id: string;
  isSearched: boolean;
};

const Option = <T extends number | string>({
  label,
  childrenOptions,
  onChange,
  onPick,
  picked,
  checked,
  semiChecked,
  isSearched,
  id,
}: Props<T>) => {
  const [expanded, setExpanded] = useState(false);
  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const isNested = childrenOptions && Array.isArray(childrenOptions);
  const open = isSearched || expanded;

  return (
    <>
      <ListItem
        disablePadding
        key={id}
        onClick={handleToggle}
        sx={{
          cursor: 'pointer',
        }}
        secondaryAction={
          <Checkbox
            checked={checked}
            edge="end"
            indeterminate={!checked && semiChecked}
            onChange={onChange}
            onClick={e => e.stopPropagation()}
            size="small"
          />
        }
      >
        <IconButton>
          {isNested && (
            <SvgIcon
              color="primary"
              fontSize="small"
              sx={{
                pr: 0.2,
                transform: `rotate(${open ? 0 : -90}deg)`,
              }}
            >
              <IconChevronDown />
            </SvgIcon>
          )}
        </IconButton>
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              variant: 'body2',
              fontWeight: isNested ? 'bold' : undefined,
            },
          }}
          sx={{
            flex: '0 0 auto',
            maxWidth: '100%',
          }}
        />
      </ListItem>
      {isNested && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack component={List} disablePadding sx={{ pl: 4 }}>
            {childrenOptions.map(item => (
              <ListItem
                key={String(item.value)}
                disablePadding
                sx={{ display: 'flex', alignItems: 'center', pl: 2 }}
                secondaryAction={
                  <Checkbox
                    checked={picked.includes(item.value)}
                    onChange={() =>
                      onPick(
                        picked.includes(item.value)
                          ? picked.filter(p => p !== item.value)
                          : [...picked, item.value]
                      )
                    }
                    size="small"
                    edge="end"
                  />
                }
              >
                <ListItemText
                  secondary={item.label}
                  sx={{
                    flex: '0 0 auto',
                  }}
                />
              </ListItem>
            ))}
          </Stack>
        </Collapse>
      )}
    </>
  );
};

export default Option;
